import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Chip,
  Grid,
  useTheme,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
// Removed date picker imports to avoid dependency issues
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';

const TeacherDailySchedule = () => {
  const theme = useTheme();
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sectionPeriods, setSectionPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch teachers and current user on component mount
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get('/api/teachers/all/');
        setTeachers(response.data);
        
        // Try to get current user and auto-select if they're a teacher
        try {
          const userResponse = await axios.get('/api/current-user/');
          if (userResponse.data.authenticated && userResponse.data.teacher_id) {
            setSelectedTeacher(userResponse.data.teacher_id);
          }
        } catch (userError) {
          console.log('Could not fetch current user for auto-selection:', userError);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setError('Failed to load teachers');
      }
    };
    fetchTeachers();
  }, []);

  // Fetch schedule when teacher or date changes
  useEffect(() => {
    if (selectedTeacher && selectedDate) {
      fetchTeacherSchedule();
    }
  }, [selectedTeacher, selectedDate]);

  const fetchTeacherSchedule = async () => {
    setLoading(true);
    setError('');
    
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get('/schedules/api/section-periods/section_periods_by_date/', {
        params: {
          date: dateStr,
          teacher_id: selectedTeacher
        }
      });
      
      // Sort periods by start time
      const sortedPeriods = response.data.section_periods.sort((a, b) => 
        a.start_time.localeCompare(b.start_time)
      );
      
      setSectionPeriods(sortedPeriods);
    } catch (error) {
      console.error('Error fetching teacher schedule:', error);
      setError('Failed to load schedule data');
      setSectionPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getPeriodTypeIcon = (periodType) => {
    switch (periodType) {
      case 'homeroom':
        return <HomeIcon color="primary" />;
      case 'cte_flexible':
        return <BuildIcon color="secondary" />;
      default:
        return <SchoolIcon color="action" />;
    }
  };

  const getPeriodTypeColor = (periodType) => {
    switch (periodType) {
      case 'homeroom':
        return theme.palette.primary.light;
      case 'cte_flexible':
        return theme.palette.secondary.light;
      default:
        return theme.palette.grey[100];
    }
  };

  const getGroupTypeChip = (group) => {
    const groupName = group.name || 'Unknown Group';
    
    if (groupName.toLowerCase().includes('homeroom')) {
      return <Chip label={groupName} color="primary" size="small" icon={<HomeIcon />} />;
    } else if (groupName.toLowerCase().includes('cte')) {
      return <Chip label={groupName} color="secondary" size="small" icon={<BuildIcon />} />;
    } else {
      return <Chip label={groupName} color="default" size="small" icon={<SchoolIcon />} />;
    }
  };

  const selectedTeacherObj = Array.isArray(teachers) ? teachers.find(t => t.id === selectedTeacher) : null;
  const selectedTeacherName = selectedTeacherObj ? 
    `${selectedTeacherObj.first_name} ${selectedTeacherObj.last_name}` : '';

  return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ScheduleIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Teacher Daily Schedule
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Controls */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="teacher-select-label">Select Teacher</InputLabel>
                <Select
                  labelId="teacher-select-label"
                  value={selectedTeacher}
                  label="Select Teacher"
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  startAdornment={<PersonIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">
                    <em>Choose a teacher...</em>
                  </MenuItem>
                  {Array.isArray(teachers) && teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Select Date"
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Schedule Display */}
          {!loading && selectedTeacher && selectedDate && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1 }} />
                Schedule for {selectedTeacherName} on {selectedDate.toLocaleDateString()}
              </Typography>

              {sectionPeriods.length === 0 ? (
                <Alert severity="info">
                  No scheduled periods found for this teacher on the selected date.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {sectionPeriods.map((period) => (
                    <Grid item xs={12} key={period.id}>
                      <Card 
                        sx={{ 
                          backgroundColor: getPeriodTypeColor(period.period_type),
                          border: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {getPeriodTypeIcon(period.period_type)}
                            <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                              {formatTime(period.start_time)} - {formatTime(period.end_time)}
                            </Typography>
                            <Chip 
                              label={period.days_of_week.join(', ')} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>

                          {/* Period Type */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Period Type: {period.period_type === 'cte_flexible' ? 'CTE Flexible Block' : 
                                        period.period_type === 'homeroom' ? 'Homeroom' : 'Regular Class Period'}
                          </Typography>

                          {/* Groups */}
                          {period.groups && period.groups.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <GroupIcon sx={{ mr: 1, fontSize: 18 }} />
                                Assigned Groups:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {period.groups.map((group) => (
                                  <Box key={group.id}>
                                    {getGroupTypeChip(group)}
                                  </Box>
                                ))}
                              </Box>

                              {/* Students */}
                              {period.groups.some(group => group.students && group.students.length > 0) && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Students:
                                  </Typography>
                                  {period.groups.map((group) => (
                                    group.students && group.students.length > 0 && (
                                      <Box key={group.id} sx={{ mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                          {group.name}:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {group.students.map((student) => (
                                            <Chip 
                                              key={student.id}
                                              label={student.name}
                                              size="small"
                                              variant="outlined"
                                            />
                                          ))}
                                        </Box>
                                      </Box>
                                    )
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}

                          {(!period.groups || period.groups.length === 0) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              No groups assigned to this period yet.
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Initial State */}
          {!selectedTeacher && !loading && (
            <Alert severity="info">
              Please select a teacher and date to view their daily schedule.
            </Alert>
          )}
        </Paper>
      </Container>
  );
};

export default TeacherDailySchedule;
